import { useMemo } from 'react'

import { aggregateOrders, buildTimesheetStatusMap } from './ordersAggregator'
import { FilterValues, MyOrdersData, OrderScope, WeekRange } from '../../types'
import { buildQueryFilters } from '../../utils/dataTransformations'
import { weekRangeToApiFilters } from '../../utils/dateUtils'
import { DataClassName } from '@/Data/const'
import { Timerecord } from '@/Data/Timerecord/TimerecordDataClass'
import { TimerecordBudget } from '@/Data/TimerecordBudget/TimerecordBudgetDataClass'
import { TimerecordOrder } from '@/Data/TimerecordOrder/TimerecordOrderDataClass'
import { SchemaAttributes } from '@/Data/typesV2'
import { getSchemaName } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { getSchema } from '@/utils/getSchema'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'
import { TimeRecordDataService } from '@/Widgets/LogTimeDrawerWidget/services/timeRecordDataService'

export const useMyOrdersData = (
  monthRange: WeekRange,
  accountDirectorId: string,
  appliedFilters?: FilterValues,
  orderScope: OrderScope = 'myOrders',
): MyOrdersData => {
  const filters = buildQueryFilters({
    userId: accountDirectorId,
    userScope: 'accountDirector',
    appliedFilters,
    weekRange: monthRange,
  })

  const customerFilter = appliedFilters?.customer
  const onlyWithRevenue = appliedFilters?.showWithRevenue ?? false

  const myOrdersRaw = TimerecordOrder.all()
    .transform({ filters: { accountDirector: accountDirectorId } })
    .take()

  const allOrdersRaw = TimeRecordDataService.getOrdersForAccountDirectors()

  const ordersForScope = orderScope === 'allOrders' ? allOrdersRaw : myOrdersRaw
  const scopedOrders = customerFilter
    ? ordersForScope.filter(
        (order) => ensureString(order.get('client')) === customerFilter,
      )
    : ordersForScope

  const timeRecords = Timerecord.all().transform({ filters }).take()

  const { schema } = getSchema(getSchemaName(DataClassName.TIMERECORD_ORDER))
  const schemaAttributes = getLocalizedSchemaAttributes(
    schema?.get('attributes') as SchemaAttributes,
  )

  const typeOptions = schemaAttributes?.type?.enumValues || []
  const orderTypesMap = Object.fromEntries(
    typeOptions.map((s) => [s.value, s.title]),
  )

  const timesheetStatus = useMemo(
    () => buildTimesheetStatusMap(timeRecords),
    [timeRecords],
  )

  const { orders, customers, summaryMetrics } = useMemo(() => {
    const dateFilters = weekRangeToApiFilters(monthRange)
    const budgetForOrder = (orderId: string) =>
      TimerecordBudget.all()
        .transform({
          filters: { order: orderId, ...dateFilters },
          limit: 1,
        } as object)
        .take()[0]

    return aggregateOrders({
      orders: scopedOrders,
      budgetForOrder,
      timesheetStatus,
      orderTypesMap,
      onlyWithRevenue,
    })
  }, [
    monthRange,
    orderTypesMap,
    scopedOrders,
    timesheetStatus,
    onlyWithRevenue,
  ])

  return { orders, customers, summaryMetrics, isLoading: false }
}
